'use client';
import { cn } from '@bazaar-marketplace/utils';
import { Close, Content, Description, Overlay, Portal, Root, Title, Trigger } from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useId, useState } from 'react';

import { Button, Typography } from '~/components';
import { Ball } from '~/components/collection-card/assets';
import { useSheetStore } from '~/store/sheet-store';
import { SheetAdornment } from './assets';
import { useLatestValueRef } from '~/lib/use-latest-value-ref';
import { SheetBackButton } from '~/components/ui/sheet/sheet-back-button';

function Sheet(props: React.ComponentProps<typeof Root>) {
  const sheetId = useId();

  const [openState, setOpenState] = useState(props.open ?? false);

  const onOpenChange = (open: boolean) => {
    props.onOpenChange?.(open);
    setOpenState(open);
  };

  const open = props.open ?? openState;
  const latestRef = useLatestValueRef({ onOpenChange });

  useEffect(() => {
    if (open) {
      // Register sheet when it opens
      useSheetStore.getState().pushSheet(sheetId, () => {
        latestRef.current.onOpenChange(false);
      });

      // Cleanup: unregister when sheet closes or component unmounts
      return () => {
        useSheetStore.getState().removeSheet(sheetId);
      };
    }
  }, [open, sheetId, latestRef]);

  return <Root data-slot="sheet" open={open} onOpenChange={onOpenChange} {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof Trigger>) {
  return <Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof Close>) {
  return (
    <Close asChild data-slot="sheet-close" {...props}>
      <Button variant="dark-bordered" />
    </Close>
  );
}

function SheetPortal({ ...props }: React.ComponentProps<typeof Portal>) {
  return <Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof Overlay>) {
  return (
    <Overlay
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 backdrop-blur-[4px] bg-[rgba(255,255,255,0.1)]',
        className,
      )}
      data-slot="sheet-overlay"
      {...props}
    />
  );
}

function SheetContent({
  adornment,
  className,
  children,
  topAdornment,
  roundedClassName = 'rounded-t-3xl',
  childrenClassName,
  onBackClick,
  ...props
}: React.ComponentProps<typeof Content> & {
  adornment?: boolean;
  topAdornment?: boolean;
  roundedClassName?: string;
  childrenClassName?: string;
  onBackClick?: () => void;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <Content
        className={cn(
          'bg-black overflow-x-hidden overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 !max-h-[90vh]',
          roundedClassName,
          'overflow-visible',
          'z-[101]',
          className,
        )}
        data-slot="sheet-content"
        {...props}
      >
        {topAdornment ? (
          <Image
            alt="Ball"
            className="absolute left-[50%] translate-y-[-20px] top-0 translate-x-[-50%]"
            height={112}
            src={Ball}
            width={112}
          />
        ) : null}
        {adornment ? (
          <div className={cn('pointer-events-none absolute inset-0 overflow-hidden', roundedClassName)}>
            <Image
              alt="Sheet adornment"
              className="size-[717] absolute translate-y-[-10%]"
              height={717}
              src={SheetAdornment}
              width={717}
            />
          </div>
        ) : null}
        <div className={cn('relative px-6 h-full flex flex-col', childrenClassName)}>{children}</div>

        {onBackClick ? <SheetBackButton onClick={onBackClick} /> : null}

        <Close className="absolute top-[24px] right-[24px] rounded-xl transition-opacity hover:opacity-90 disabled:pointer-events-none">
          <div className="flex items-center justify-center p-3 bg-white/10 backdrop-blur-[25px] border border-white/20 rounded-2xl">
            <XIcon className="size-4 text-white stroke-2" />
          </div>
        </Close>
      </Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={cn('flex flex-col gap-1.5 font-quicksand mt-12', className)} data-slot="sheet-header" {...props} />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} data-slot="sheet-footer" {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof Title>) {
  return (
    <Title className={cn('text-foreground font-semibold', className)} data-slot="sheet-title" {...props}>
      <Typography className="font-quicksand" size="2xl" weight="regular">
        {props.children}
      </Typography>
    </Title>
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof Description>) {
  return (
    <Description
      className={cn('text-muted-foreground text-sm', className)}
      data-slot="sheet-description"
      {...props}
      asChild
    >
      <div>
        <Typography className="text-white opacity-80" size="md" weight="regular">
          {props.children}
        </Typography>
      </div>
    </Description>
  );
}

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger };
